/* import __COLOCATED_TEMPLATE__ from './custom-time-sla-edit-drawer.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-management */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency-decorators';
import Delays from 'embercom/lib/workflows/helpers/delays';
import type IntlService from 'ember-intl/services/intl';
import type OfficeHoursSchedule from 'embercom/models/office-hours-schedule';
import type ConversationSla from 'embercom/models/inbox/conversation-sla';
import type { TaskGenerator } from 'ember-concurrency';

const DEFAULT_MINIMUM_SLA_SECONDS = 30;
const TINY_MINIMUM_SLA_SECONDS = 9;
const MAX_SLA_SECONDS = 8640000;

interface Args {
  sla: ConversationSla;
  conversationSlas: ConversationSla[];
  officeHoursSchedules: OfficeHoursSchedule[];
  onClose: () => void;
  isOpen: boolean;
}

interface Signature {
  Args: Args;
}

export default class CustomTimeSlaEditDrawer extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: any;

  private delays = new Delays();

  @tracked slaName: string;
  @tracked firstReplyDelayUnit: string;
  @tracked firstReplyDelayValue: number;
  @tracked nextReplyDelayUnit: string;
  @tracked nextReplyDelayValue: number;
  @tracked resolutionDelayUnit: string;
  @tracked resolutionDelayValue: number;
  @tracked timeToCloseDelayUnit: string;
  @tracked timeToCloseDelayValue: number;
  @tracked selectedScheduleId: string | undefined;
  @tracked pauseOnWaitingOnCustomer: boolean;
  @tracked pauseOnSnooze: boolean;

  private firstReplyInitiallySet: boolean;
  private nextReplyInitiallySet: boolean;
  private resolutionInitiallySet: boolean;
  private timeToCloseInitiallySet: boolean;

  constructor(owner: unknown, args: Args) {
    super(owner, args);

    let { sla } = args;
    let { delays } = this;

    this.slaName = sla.name ?? '';

    let firstReplySeconds = sla.firstReplyTimeInSeconds ?? null;
    let nextReplySeconds = sla.nextReplyTimeInSeconds ?? null;
    let resolutionSeconds = sla.resolutionTimeInSeconds ?? null;
    let timeToCloseSeconds = sla.timeToCloseInSeconds ?? null;

    this.firstReplyInitiallySet = firstReplySeconds !== null;
    this.nextReplyInitiallySet = nextReplySeconds !== null;
    this.resolutionInitiallySet = resolutionSeconds !== null;
    this.timeToCloseInitiallySet = timeToCloseSeconds !== null;

    this.firstReplyDelayUnit = firstReplySeconds
      ? delays.getDelayUnit(firstReplySeconds)
      : 'second';
    this.firstReplyDelayValue = firstReplySeconds ? delays.getDelayValue(firstReplySeconds) : 0;

    this.nextReplyDelayUnit = nextReplySeconds ? delays.getDelayUnit(nextReplySeconds) : 'second';
    this.nextReplyDelayValue = nextReplySeconds ? delays.getDelayValue(nextReplySeconds) : 0;

    this.resolutionDelayUnit = resolutionSeconds
      ? delays.getDelayUnit(resolutionSeconds)
      : 'second';
    this.resolutionDelayValue = resolutionSeconds ? delays.getDelayValue(resolutionSeconds) : 0;

    this.timeToCloseDelayUnit = timeToCloseSeconds
      ? delays.getDelayUnit(timeToCloseSeconds)
      : 'second';
    this.timeToCloseDelayValue = timeToCloseSeconds ? delays.getDelayValue(timeToCloseSeconds) : 0;

    this.pauseOnSnooze = sla.pauseOnSnooze ?? false;
    this.pauseOnWaitingOnCustomer = sla.pauseOnWaitingOnCustomer ?? false;
    this.selectedScheduleId = sla.officeHoursScheduleId ?? this.defaultScheduleId;
  }

  private get defaultScheduleId(): string | undefined {
    let schedules = this.args.officeHoursSchedules;
    let schedule = schedules.findBy('isDefault', true);
    if (schedule) {
      return schedule.id;
    } else if (schedules.length > 0) {
      return schedules.firstObject?.id;
    } else {
      return undefined;
    }
  }

  private timeInSeconds(value: number, unit: string): number | null {
    if (Number(value) === 0) {
      return null;
    }
    return this.delays.getDelayInSeconds(value, unit);
  }

  get firstReplyTimeInSeconds() {
    return this.timeInSeconds(this.firstReplyDelayValue, this.firstReplyDelayUnit);
  }

  get nextReplyTimeInSeconds() {
    return this.timeInSeconds(this.nextReplyDelayValue, this.nextReplyDelayUnit);
  }

  get resolutionTimeInSeconds() {
    return this.timeInSeconds(this.resolutionDelayValue, this.resolutionDelayUnit);
  }

  get timeToCloseInSeconds() {
    return this.timeInSeconds(this.timeToCloseDelayValue, this.timeToCloseDelayUnit);
  }

  get trimmedSlaName() {
    return (this.slaName || '').trim();
  }

  get duplicatedName() {
    return this.args.conversationSlas
      .filter((sla) => sla.id !== this.args.sla.id && !sla.isNew)
      .any((sla) => sla.name === this.trimmedSlaName);
  }

  get firstReplyMin() {
    return this.firstReplyInitiallySet ? 1 : 0;
  }

  get nextReplyMin() {
    return this.nextReplyInitiallySet ? 1 : 0;
  }

  get resolutionMin() {
    return this.resolutionInitiallySet ? 1 : 0;
  }

  get timeToCloseMin() {
    return this.timeToCloseInitiallySet ? 1 : 0;
  }

  get minimumSlaSeconds() {
    return this.appService.app.canUseFeature('tiny-minimum-sla-duration')
      ? TINY_MINIMUM_SLA_SECONDS
      : DEFAULT_MINIMUM_SLA_SECONDS;
  }

  private isTimeInRange(seconds: number | null) {
    return seconds === null || (seconds >= this.minimumSlaSeconds && seconds <= MAX_SLA_SECONDS);
  }

  get firstReplyTimeValid() {
    return this.isTimeInRange(this.firstReplyTimeInSeconds);
  }

  get nextReplyTimeValid() {
    return this.isTimeInRange(this.nextReplyTimeInSeconds);
  }

  get resolutionTimeValid() {
    return this.isTimeInRange(this.resolutionTimeInSeconds);
  }

  get timeToCloseValid() {
    if (!this.isTimeInRange(this.timeToCloseInSeconds)) {
      return false;
    }
    if (
      this.resolutionTimeInSeconds !== null &&
      this.timeToCloseInSeconds !== null &&
      this.timeToCloseInSeconds < this.resolutionTimeInSeconds
    ) {
      return false;
    }
    return true;
  }

  get removedExistingTarget() {
    return (
      (this.firstReplyInitiallySet && this.firstReplyTimeInSeconds === null) ||
      (this.nextReplyInitiallySet && this.nextReplyTimeInSeconds === null) ||
      (this.resolutionInitiallySet && this.resolutionTimeInSeconds === null) ||
      (this.timeToCloseInitiallySet && this.timeToCloseInSeconds === null)
    );
  }

  get noValuesSelected() {
    return (
      this.firstReplyTimeInSeconds === null &&
      this.nextReplyTimeInSeconds === null &&
      this.resolutionTimeInSeconds === null &&
      this.timeToCloseInSeconds === null
    );
  }

  get timeValidationError() {
    if (!this.firstReplyTimeValid || !this.nextReplyTimeValid) {
      if (this.appService.app.canUseFeature('tiny-minimum-sla-duration')) {
        return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-response-times-dynamic', {
          minimumSeconds: this.minimumSlaSeconds,
        });
      }
      return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-response-times');
    }
    if (!this.resolutionTimeValid || !this.timeToCloseValid) {
      if (this.appService.app.canUseFeature('tiny-minimum-sla-duration')) {
        return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-completion-times-dynamic', {
          minimumSeconds: this.minimumSlaSeconds,
        });
      }
      return this.intl.t('inbox.sla.custom-time-sla-drawer.invalid-completion-times');
    }
    if (this.removedExistingTarget) {
      return this.intl.t('settings.data.sla.edit-modal.cannot-remove-existing-target');
    }
    if (this.noValuesSelected) {
      return this.intl.t('settings.data.sla.edit-modal.at-least-one-target-required');
    }
    return null;
  }

  get saveButtonDisabled() {
    let invalidName = this.trimmedSlaName.length === 0 || this.duplicatedName;
    return invalidName || !!this.timeValidationError;
  }

  get appHasMoreThanOneOfficeHours() {
    return this.args.officeHoursSchedules.length > 1;
  }

  get dropdownSchedules() {
    return this.args.officeHoursSchedules.map((schedule) => ({
      value: schedule.id,
      text: schedule.name,
      description: schedule.labelWithTimezone,
    }));
  }

  @task({ drop: true })
  *saveSla(): TaskGenerator<void> {
    let sla = this.args.sla;
    sla.name = this.trimmedSlaName;
    sla.firstReplyTimeInSeconds = this.firstReplyTimeInSeconds as number;
    sla.nextReplyTimeInSeconds = this.nextReplyTimeInSeconds as number;
    sla.resolutionTimeInSeconds = this.resolutionTimeInSeconds as number;
    sla.timeToCloseInSeconds = this.timeToCloseInSeconds as number;
    sla.pauseOnSnooze = this.pauseOnSnooze;
    sla.pauseOnWaitingOnCustomer = this.pauseOnWaitingOnCustomer;
    sla.officeHoursScheduleId = this.selectedScheduleId as string;
    try {
      yield sla.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.data.sla.edit-modal.save-success'),
      );
      this.args.onClose();
    } catch {
      sla.rollbackAttributes();
      this.notificationsService.notifyError(this.intl.t('settings.data.sla.edit-modal.save-error'));
    }
  }

  @action setFirstReplyDelayUnit(unit: string) {
    this.firstReplyDelayUnit = unit;
  }

  @action setNextReplyDelayUnit(unit: string) {
    this.nextReplyDelayUnit = unit;
  }

  @action setResolutionDelayUnit(unit: string) {
    this.resolutionDelayUnit = unit;
  }

  @action setTimeToCloseDelayUnit(unit: string) {
    this.timeToCloseDelayUnit = unit;
  }

  @action setSelectedScheduleId(id: string) {
    this.selectedScheduleId = id;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox::Slas::CustomTimeSlaEditDrawer': typeof CustomTimeSlaEditDrawer;
    'inbox/slas/custom-time-sla-edit-drawer': typeof CustomTimeSlaEditDrawer;
  }
}
